// src/components/empresas/CertificadosPage/CertificadosContainer.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import CertificadosPresentation from './CertificadosPresentation';
import { useApi, useNotification } from '../../../hooks';
import { empresaService } from '../../../services/empresaService';
import { addCertificado, removeCertificado, setCertificados } from '../../../redux/empresaSlice';

const CertificadosContainer = () => {
  const { rutEmpresa } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { empresas, certificados, empresaActiva } = useSelector(state => state.empresa);
  const { showNotification } = useNotification();

  // Estados locales
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [certificadoSeleccionado, setCertificadoSeleccionado] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [errorCerts, setErrorCerts] = useState(null);


  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    password: '',
    fecha_expiracion: null,
    activo: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [certificadoFile, setCertificadoFile] = useState(null);

  // API hooks (solo para cargar y eliminar certificados)
  const {
    loading: cargandoCertificado,
    error: errorCargando,
    execute: cargarCertificado
  } = useApi(empresaService.cargarCertificado);

  const {
    loading: eliminandoCertificado,
    error: errorEliminando,
    execute: eliminarCertificado
  } = useApi(empresaService.eliminarCertificado);

  // Ref para evitar doble carga
  const lastLoadedRut = useRef(null);

  // Determinar el RUT activo y cargar datos
  const rutActivo = rutEmpresa || empresaActiva?.rut;

  // Resolver empresa desde Redux cuando cambia la lista o el rut
  useEffect(() => {
    const emp = empresas.find(e => e.rut === rutActivo) || null;
    setEmpresa(emp);
  }, [empresas, rutActivo]);

  useEffect(() => {
    if (!rutActivo) {
      navigate('/empresas');
      return;
    }

    // Redirigir a URL con rut si no lo tiene
    if (!rutEmpresa && rutActivo) {
      navigate(`/certificados/${rutActivo}`, { replace: true });
    }

    // Cargar certificados solo si cambió el RUT
    if (lastLoadedRut.current !== rutActivo) {
      lastLoadedRut.current = rutActivo;
      dispatch(setCertificados([]));
      setLoadingCerts(true);
      setErrorCerts(null);
      empresaService.getCertificados(rutActivo, false)
        .then(data => {
          dispatch(setCertificados(data));
        })
        .catch(err => {
          console.error('Error al cargar certificados:', err);
          setErrorCerts(err.message || 'Error al cargar certificados');
        })
        .finally(() => {
          setLoadingCerts(false);
        });
    }
  }, [rutActivo, rutEmpresa, dispatch, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handlers para formulario
  const handleInputChange = useCallback((e) => {
    const { name, value, checked } = e.target;
    if (name === 'activo') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [formErrors]);

  const handleDateChange = useCallback((newValue) => {
    setFormData(prev => ({
      ...prev,
      fecha_expiracion: newValue ? newValue.toDate() : null
    }));
  }, []);

  const handleFileChange = useCallback((e) => {
    if (e.target.files.length > 0) {
      setCertificadoFile(e.target.files[0]);
      if (formErrors.certificadoFile) {
        setFormErrors(prev => ({ ...prev, certificadoFile: '' }));
      }
    }
  }, [formErrors]);

  const handleToggleShowPassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  // Validar formulario
  const validateForm = useCallback(() => {
    const errors = {};
    if (!formData.nombre) errors.nombre = 'El nombre es obligatorio';
    if (!formData.password) errors.password = 'La contraseña es obligatoria';
    if (!certificadoFile) errors.certificadoFile = 'Debe seleccionar un archivo de certificado';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, certificadoFile]);

  // Handlers para diálogos
  const handleOpenDialog = useCallback(() => {
    setOpenDialog(true);
    setFormData({ nombre: '', password: '', fecha_expiracion: null, activo: true });
    setCertificadoFile(null);
    setFormErrors({});
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
  }, []);

  const handleOpenDeleteDialog = useCallback((certificado) => {
    setCertificadoSeleccionado(certificado);
    setOpenDeleteDialog(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setOpenDeleteDialog(false);
  }, []);

  // Submit del formulario - usa rut_empresa
  const handleSubmit = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validateForm()) return;

    const rutEmpresaSubmit = empresa?.rut || rutActivo;
    if (!rutEmpresaSubmit) {
      showNotification('No se pudo determinar la empresa', 'error');
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('certificado_file', certificadoFile);
      formDataToSend.append('rut_empresa', rutEmpresaSubmit);
      formDataToSend.append('nombre', formData.nombre);
      formDataToSend.append('password', formData.password);
      formDataToSend.append('activo', formData.activo ? 1 : 0);

      if (formData.fecha_expiracion) {
        const fecha = formData.fecha_expiracion;
        const year = fecha.getFullYear();
        const month = String(fecha.getMonth() + 1).padStart(2, '0');
        const day = String(fecha.getDate()).padStart(2, '0');
        formDataToSend.append('fecha_expiracion', `${year}-${month}-${day}`);
      }

      const data = await cargarCertificado(formDataToSend);
      dispatch(addCertificado(data));
      showNotification('Certificado cargado correctamente', 'success');
      handleCloseDialog();
    } catch (err) {
      console.error('Error al cargar certificado:', err);
      showNotification(err.message || 'Error al cargar certificado', 'error');
    }
  }, [validateForm, empresa, formData, certificadoFile, cargarCertificado, dispatch, showNotification, handleCloseDialog]);

  const handleDeleteCertificado = useCallback(async () => {
    if (!certificadoSeleccionado) return;

    try {
      await eliminarCertificado(certificadoSeleccionado.id);
      dispatch(removeCertificado(certificadoSeleccionado.id));
      showNotification('Certificado eliminado correctamente', 'success');
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error al eliminar certificado:', err);
    }
  }, [certificadoSeleccionado, eliminarCertificado, dispatch, showNotification, handleCloseDeleteDialog]);

  // Helpers
  const isCertificadoExpired = useCallback((certificado) => {
    if (!certificado.fecha_expiracion) return false;
    return new Date(certificado.fecha_expiracion) < new Date();
  }, []);

  const isCertificadoExpiringInSoon = useCallback((certificado) => {
    if (!certificado.fecha_expiracion) return false;
    const fechaExpiracion = new Date(certificado.fecha_expiracion);
    const hoy = new Date();
    const treintaDias = new Date();
    treintaDias.setDate(hoy.getDate() + 30);
    return fechaExpiracion >= hoy && fechaExpiracion <= treintaDias;
  }, []);

  const getCertificadoStatus = useCallback((certificado) => {
    if (!certificado.activo) return { label: 'Inactivo', color: 'default' };
    if (isCertificadoExpired(certificado)) return { label: 'Expirado', color: 'error' };
    if (isCertificadoExpiringInSoon(certificado)) return { label: 'Por expirar', color: 'warning' };
    return { label: 'Activo', color: 'success' };
  }, [isCertificadoExpired, isCertificadoExpiringInSoon]);


  const handleNavigateToEmpresas = useCallback(() => {
    navigate('/empresas');
  }, [navigate]);

  return (
    <CertificadosPresentation
      empresa={empresa}
      certificados={certificados}
      certificadoSeleccionado={certificadoSeleccionado}
      openDialog={openDialog}
      openDeleteDialog={openDeleteDialog}
      showPassword={showPassword}
      formData={formData}
      formErrors={formErrors}
      certificadoFile={certificadoFile}
      loading={loadingCerts}
      cargandoCertificado={cargandoCertificado}
      eliminandoCertificado={eliminandoCertificado}
      error={errorCerts}
      errorCargando={errorCargando}
      errorEliminando={errorEliminando}
      onNavigateToEmpresas={handleNavigateToEmpresas}
      onOpenDialog={handleOpenDialog}
      onCloseDialog={handleCloseDialog}
      onOpenDeleteDialog={handleOpenDeleteDialog}
      onCloseDeleteDialog={handleCloseDeleteDialog}
      onDeleteCertificado={handleDeleteCertificado}
      onInputChange={handleInputChange}
      onDateChange={handleDateChange}
      onFileChange={handleFileChange}
      onToggleShowPassword={handleToggleShowPassword}
      onSubmit={handleSubmit}
      getCertificadoStatus={getCertificadoStatus}
      isCertificadoExpired={isCertificadoExpired}
      isCertificadoExpiringInSoon={isCertificadoExpiringInSoon}
    />
  );
};

export default CertificadosContainer;
